import { useState } from "react";
import { Link } from "react-router-dom";
import { useMyDevices } from "../../hooks/useMyDevices";
import {
  DEVICE_HEALTH_META,
  ingestKeyStatus,
  worseHealth,
  type DeviceHealth,
  type DeviceStatus,
} from "../../lib/deviceStatus";
import type { IngestKey } from "../../api/types";

const SHORT_LABEL: Record<DeviceHealth, string> = {
  ok: "Kit à jour",
  outdated: "Kit à mettre à jour",
  silent: "Kit muet",
};

interface Props {
  // L'enrôlement d'un poste est réservé aux admins (onglet Postes de /admin).
  canEnroll: boolean;
  onNavigate?: () => void;
}

// État du kit de feuilles de temps sur les postes du salarié connecté : le
// pire des postes, le détail de chacun au survol ou au clic. Rien tant que la
// liste n'est pas lue : « non installé » serait faux si l'API manque.
export function KitStatus({ canEnroll, onNavigate }: Props) {
  const { data, dataUpdatedAt } = useMyDevices();
  const [open, setOpen] = useState(false);
  if (!data) return null;

  const now = new Date(dataUpdatedAt || Date.now());
  const rows = data.devices
    .map((k) => ({
      key: k,
      status: ingestKeyStatus(k, data.client_versions, now),
    }))
    .filter((r): r is { key: IngestKey; status: DeviceStatus } => !!r.status);

  if (rows.length === 0) {
    return (
      <div className="kit-status" data-health="none">
        <span className="kit-dot" aria-hidden="true" />
        <span className="kit-lbl">Kit non installé</span>
        {canEnroll && (
          <Link to="/admin?tab=postes" className="kit-link" onClick={onNavigate}>
            Enrôler
          </Link>
        )}
      </div>
    );
  }

  const worst = rows.reduce<DeviceHealth>(
    (acc, r) => worseHealth(acc, r.status.health),
    "ok",
  );
  const detail = rows
    .map((r) => `${r.key.label} : ${r.status.label}`)
    .join("\n");

  return (
    <div className="kit-status" data-health={worst}>
      <button
        type="button"
        className="kit-toggle"
        title={detail}
        aria-expanded={open}
        aria-controls="kit-status-detail"
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className="kit-dot"
          role="img"
          aria-label={DEVICE_HEALTH_META[worst].title}
          style={{ background: DEVICE_HEALTH_META[worst].color }}
        />
        <span className="kit-lbl">{SHORT_LABEL[worst]}</span>
      </button>
      {open && (
        <ul id="kit-status-detail" className="kit-detail">
          {rows.map((r) => (
            <li key={r.key.id} data-health={r.status.health}>
              <span
                className="kit-dot"
                aria-hidden="true"
                style={{
                  background: DEVICE_HEALTH_META[r.status.health].color,
                }}
              />
              <span>
                <span className="kit-name">{r.key.label}</span>
                <span className="kit-sub">{r.status.label}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
